import React, { useMemo, useRef } from 'react';

import { diff } from 'deep-object-diff';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import { useCreateProjectsDashboardsFilterSelectionConfigurationsBulkUpdateProcessMutation } from 'components/hooks/projectsDashboardsFilterSelectionConfigurationsBulkUpdateProcessMutations';
import { useCreateProjectsDashboardsTableConfigurationsBulkUpdateProcessMutation } from 'components/hooks/projectsDashboardsTableConfigurationsBulkUpdateProcessMutations';
import useDocumentVisibilityChange from 'components/hooks/useDocumentVisibilityChange';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import ArchivedProjectsIndex from 'components/projectsDashboard/components/ArchivedProjectsIndex';
import FoldersIndex from 'components/projectsDashboard/components/FoldersIndex';
import ProjectsIndex from 'components/projectsDashboard/components/ProjectsIndex';
import {
  buildBulkUpdateFilterSelectionConfigurationsParams,
  buildBulkUpdateTableConfigurationsParams,
} from 'components/projectsDashboard/helpers';
import useProjectFilters, {
  buildBaseProjectFilters,
} from 'components/projectsDashboard/hooks/useProjectFilters';
import useProjectTableConfiguration from 'components/projectsDashboard/hooks/useProjectTableConfiguration';

export default function IndexWrapper({
  activeSidebarMenuSection,
  defaultCurrentSearch,
  domainProjectsDashboardFilterSelectionConfiguration,
  domainProjectsDashboardTableConfiguration,
  hasFolders,
  indexWrapperRef,
  onArchiveProjectsClick,
  onDebouncedFiltersChange,
  onDeleteFolderClick,
  onFolderClick,
  onGroupIntoFolderClick,
  onMoveToFolderClick,
  onNewFolderClick,
  onProjectArchiveClose,
  onProjectDestroyClose,
  onProjectToggle,
  onProjectsPageChange,
  onRenameFolderClick,
  onSelectAllProjectsToggle,
  onSidebarMenuSectionClick,
  selectedFolderId,
  selectedProjectIds,
}) {
  const {
    account: {
      attributes: { projectReferenceLabel },
    },
    division: {
      attributes: { singularRAMSName },
      id: divisionId,
    },
  } = useCurrentActor();

  const {
    folderPolicy,
    projectPolicy,
    projectsArchivePolicy,
    projectsBulkArchivePolicy,
    projectsBulkFolderAssignmentPolicy,
    projectsDashboardPolicy,
    projectsDuplicationProcessPolicy,
    seedDashboardConfigurableFields,
  } = useProjectsDashboardContext();

  const { t: i18nTCommon } = useTranslation('common');

  const configurableFieldOptionFilterSelections = buildHasManyRelations({
    inclusionType: 'configurableFieldOptionFilterSelection',
    relationType: 'configurableFieldOptionFilterSelections',
    resource: domainProjectsDashboardFilterSelectionConfiguration,
  });

  const dateFilterSelections = buildHasManyRelations({
    inclusionType: 'dateFilterSelection',
    relationType: 'dateFilterSelections',
    resource: domainProjectsDashboardFilterSelectionConfiguration,
  });

  const folderFilterSelections = buildHasManyRelations({
    inclusionType: 'folderFilterSelection',
    relationType: 'folderFilterSelections',
    resource: domainProjectsDashboardFilterSelectionConfiguration,
  });

  const optionFilterSelections = buildHasManyRelations({
    inclusionType: 'optionFilterSelection',
    relationType: 'optionFilterSelections',
    resource: domainProjectsDashboardFilterSelectionConfiguration,
  });

  const userFilterSelections = buildHasManyRelations({
    inclusionType: 'userFilterSelection',
    relationType: 'userFilterSelections',
    resource: domainProjectsDashboardFilterSelectionConfiguration,
  });

  const {
    handleProjectFilterAdd,
    handleProjectFilterRemove,
    handleProjectFilterValueAdd,
    handleProjectFilterValueChange,
    handleProjectFilterValueRemove,
    handleProjectFiltersCleanup,
    handleProjectFiltersReset,
    projectFilters,
  } = useProjectFilters({
    configurableFieldOptionFilterSelections,
    dashboardConfigurableFields: seedDashboardConfigurableFields.data,
    dateFilterSelections,
    folderFilterSelections,
    optionFilterSelections,
    userFilterSelections,
  });

  const tableSortConfiguration = buildHasOneRelation({
    inclusionType: 'tableSortConfiguration',
    relationType: 'tableSortConfiguration',
    resource: domainProjectsDashboardTableConfiguration,
  });

  const tableColumnConfigurations = buildHasManyRelations({
    inclusionType: 'tableColumnConfiguration',
    relationType: 'tableColumnConfigurations',
    resource: domainProjectsDashboardTableConfiguration,
  });

  const {
    handleProjectTableColumnOrderChange,
    handleProjectTableColumnSizingChange,
    handleProjectTableColumnSortChange,
    handleProjectTableColumnVisibilityChange,
    handleProjectTableConfigurationsReset,
    projectTableConfiguration,
  } = useProjectTableConfiguration({
    canAction:
      projectPolicy.canDestroy ||
      projectsArchivePolicy.canCreate ||
      projectsDuplicationProcessPolicy.canCreate,
    canSelect:
      folderPolicy.canCreate ||
      projectsBulkArchivePolicy.canCreate ||
      projectsBulkFolderAssignmentPolicy.canCreate,
    dashboardConfigurableFields: seedDashboardConfigurableFields.data,
    projectReferenceLabel,
    singularRAMSName,
    tableColumnConfigurations,
    tableSortConfiguration,
    translations: {
      approvalStatus: i18nTCommon('projects.approval'),
      approver: i18nTCommon('projects.approver'),
      creationDate: i18nTCommon('shared.created'),
      creator: i18nTCommon('projects.creator'),
      folder: i18nTCommon('projects.folder'),
      lastUpdated: i18nTCommon('shared.lastUpdated'),
      live: i18nTCommon('shared.live'),
      reference: i18nTCommon('shared.reference'),
      reviewDate: i18nTCommon('projects.reviewDate'),
      reviewer: i18nTCommon('projects.reviewer'),
      riskAssessments: i18nTCommon('projects.riskAssessments'),
      signatureStatus: i18nTCommon('projects.signature'),
      startDate: i18nTCommon('shared.startDate'),
    },
    ...projectsDashboardPolicy,
  });

  const baseProjectFilters = buildBaseProjectFilters({
    dashboardConfigurableFields: seedDashboardConfigurableFields.data,
    translations: {
      approvalStatus: i18nTCommon('projects.approvalStatus'),
      approver: i18nTCommon('projects.approver'),
      creationDate: i18nTCommon('shared.creationDate'),
      creator: i18nTCommon('projects.creator'),
      folder: i18nTCommon('projects.folder'),
      lastUpdated: i18nTCommon('shared.lastUpdated'),
      lifecycleStatus: i18nTCommon('projects.projectStatus', {
        interpolation: { escapeValue: false },
        singularRAMSName: voca.capitalize(singularRAMSName),
      }),
      reviewDate: i18nTCommon('projects.reviewDate'),
      reviewer: i18nTCommon('projects.reviewer'),
      reviewStatus: i18nTCommon('projects.reviewStatus'),
      signatureStatus: i18nTCommon('projects.signatureStatus'),
      startDate: i18nTCommon('shared.startDate'),
    },
  });

  const visibleProjectFilters = useMemo(() => {
    const hiddenProjectFilters = [];

    if (!!selectedFolderId || !projectsDashboardPolicy.isFolderConfigurable)
      hiddenProjectFilters.push('folderFilter');
    if (!projectsDashboardPolicy.isApprovalConfigurable)
      hiddenProjectFilters.push('approvalStatusFilter', 'approverFilter');
    if (!projectsDashboardPolicy.isReviewConfigurable)
      hiddenProjectFilters.push(
        'reviewStatusFilter',
        'reviewDateFilter',
        'reviewerFilter',
      );
    if (!projectsDashboardPolicy.isSignatureConfigurable)
      hiddenProjectFilters.push('signatureStatusFilter');

    return Array.from(baseProjectFilters.keys()).filter(
      (filterKey) => !hiddenProjectFilters.includes(filterKey),
    );
  }, [
    selectedFolderId,
    ...Object.values(projectsDashboardPolicy),
    ...Array.from(baseProjectFilters.entries()),
  ]);

  // used by document visibilty change hook to diff initial and current project filters
  const initialDocumentVisibilityChangeProjectFilters = useRef({
    filters: projectFilters,
    visibleFilters: visibleProjectFilters,
  });
  const documentVisibilityChangeProjectFilters = useRef();
  documentVisibilityChangeProjectFilters.current = {
    filters: projectFilters,
    visibleFilters: visibleProjectFilters,
  };

  const createFilterSelectionConfigurationsBulkUpdateProcessMutation =
    useCreateProjectsDashboardsFilterSelectionConfigurationsBulkUpdateProcessMutation();

  useDocumentVisibilityChange((visibilityState) => {
    if (visibilityState === 'visible') {
      initialDocumentVisibilityChangeProjectFilters.current =
        documentVisibilityChangeProjectFilters.current;
    }

    const documentVisibilityChangeProjectFiltersDiff = diff(
      initialDocumentVisibilityChangeProjectFilters.current,
      documentVisibilityChangeProjectFilters.current,
    );

    if (
      visibilityState === 'hidden' &&
      Object.keys(documentVisibilityChangeProjectFiltersDiff).length > 0
    ) {
      createFilterSelectionConfigurationsBulkUpdateProcessMutation.mutate({
        bulkUpdateProcessParams:
          buildBulkUpdateFilterSelectionConfigurationsParams(
            documentVisibilityChangeProjectFilters.current,
          ),
        divisionId,
      });
    }
  });

  // used by document visibilty change hook to diff initial and current table configuration
  const initialDocumentVisibilityChangeProjectTableConfiguration = useRef({
    columnOrder: projectTableConfiguration.columnOrder,
    columnVisibility: projectTableConfiguration.columnVisibility,
    columnSort: projectTableConfiguration.columnSort,
    columnSizingDefaults: projectTableConfiguration.columnSizingDefaults,
    columnSizing: projectTableConfiguration.columnSizing,
    isReset: projectTableConfiguration.isReset,
  });
  const documentVisibilityChangeProjectTableConfiguration = useRef();
  documentVisibilityChangeProjectTableConfiguration.current = {
    columnOrder: projectTableConfiguration.columnOrder,
    columnVisibility: projectTableConfiguration.columnVisibility,
    columnSort: projectTableConfiguration.columnSort,
    columnSizingDefaults: projectTableConfiguration.columnSizingDefaults,
    columnSizing: projectTableConfiguration.columnSizing,
    isReset: projectTableConfiguration.isReset,
  };

  const createTableConfigurationsBulkUpdateProcessMutation =
    useCreateProjectsDashboardsTableConfigurationsBulkUpdateProcessMutation();

  useDocumentVisibilityChange((visibilityState) => {
    if (visibilityState === 'visible') {
      initialDocumentVisibilityChangeProjectTableConfiguration.current =
        documentVisibilityChangeProjectTableConfiguration.current;
    }

    const documentVisibilityChangeProjectTableConfigurationDiff = diff(
      initialDocumentVisibilityChangeProjectTableConfiguration.current,
      documentVisibilityChangeProjectTableConfiguration.current,
    );

    if (
      visibilityState === 'hidden' &&
      Object.keys(documentVisibilityChangeProjectTableConfigurationDiff)
        .length > 0
    ) {
      const bulkUpdateProcessParams =
        documentVisibilityChangeProjectTableConfiguration.current.isReset ?
          { columns: [], sort: null }
        : buildBulkUpdateTableConfigurationsParams({
            tableConfiguration: {
              ...documentVisibilityChangeProjectTableConfiguration.current,
              columnSizing:
                documentVisibilityChangeProjectTableConfigurationDiff.columnSizing,
            },
          });

      createTableConfigurationsBulkUpdateProcessMutation.mutate({
        bulkUpdateProcessParams,
        divisionId,
      });
    }
  });

  return (
    <div
      className='tw-w-full tw-flex-1 tw-overflow-y-auto tw-pb-36 tw-pt-6'
      ref={indexWrapperRef}
    >
      {activeSidebarMenuSection === 'folders' && folderPolicy.canIndex ?
        <FoldersIndex
          defaultCurrentSearch={defaultCurrentSearch}
          onDeleteClick={onDeleteFolderClick}
          onFolderClick={({ id }) => {
            onFolderClick({
              folderId: id,
              section: 'recents',
            });
          }}
          onNewClick={onNewFolderClick}
          onRenameClick={onRenameFolderClick}
        />
      : activeSidebarMenuSection === 'archive' ?
        <ArchivedProjectsIndex
          defaultCurrentSearch={defaultCurrentSearch}
          onCurrentSectionClick={() => {
            onSidebarMenuSectionClick({ section: 'current' });
          }}
        />
      : <ProjectsIndex
          baseFilters={baseProjectFilters}
          columnOrder={projectTableConfiguration.columnOrder}
          columnSizing={projectTableConfiguration.columnSizing}
          columnSort={projectTableConfiguration.columnSort}
          columnVisibility={projectTableConfiguration.columnVisibility}
          columns={projectTableConfiguration.columns}
          defaultCurrentSearch={defaultCurrentSearch}
          filters={projectFilters}
          folderId={selectedFolderId}
          hasFolders={hasFolders}
          key={selectedFolderId}
          onArchiveProjectsClick={onArchiveProjectsClick}
          onColumnOrderChange={handleProjectTableColumnOrderChange}
          onColumnSizingChange={handleProjectTableColumnSizingChange}
          onColumnSortChange={handleProjectTableColumnSortChange}
          onColumnVisibilityChange={handleProjectTableColumnVisibilityChange}
          onDebouncedFiltersChange={onDebouncedFiltersChange}
          onDeleteFolderClick={onDeleteFolderClick}
          onFilterAdd={handleProjectFilterAdd}
          onFilterRemove={handleProjectFilterRemove}
          onFilterValueAdd={handleProjectFilterValueAdd}
          onFilterValueChange={handleProjectFilterValueChange}
          onFilterValueRemove={handleProjectFilterValueRemove}
          onGroupIntoFolderClick={onGroupIntoFolderClick}
          onMoveToFolderClick={onMoveToFolderClick}
          onProjectArchiveClose={onProjectArchiveClose}
          onProjectDestroyClose={onProjectDestroyClose}
          onProjectToggle={onProjectToggle}
          onProjectsFilterSidepanelClose={handleProjectFiltersCleanup}
          onProjectsPageChange={onProjectsPageChange}
          onResetTableToDefault={() => {
            handleProjectFiltersReset();
            handleProjectTableConfigurationsReset();
          }}
          onSectionClick={onSidebarMenuSectionClick}
          onSelectAllProjectsToggle={onSelectAllProjectsToggle}
          selectedProjectIds={selectedProjectIds}
          visibleFilters={visibleProjectFilters}
        />
      }
    </div>
  );
}

IndexWrapper.propTypes = {
  activeSidebarMenuSection: PropTypes.string.isRequired,
  domainProjectsDashboardFilterSelectionConfiguration: PropTypes.shape({
    data: PropTypes.object.isRequired,
    included: PropTypes.object.isRequired,
  }),
  domainProjectsDashboardTableConfiguration: PropTypes.shape({
    data: PropTypes.object.isRequired,
    included: PropTypes.object.isRequired,
  }),
  hasFolders: PropTypes.bool.isRequired,
  indexWrapperRef: PropTypes.object.isRequired,
  onArchiveProjectsClick: PropTypes.func.isRequired,
  onDebouncedFiltersChange: PropTypes.func.isRequired,
  onDeleteFolderClick: PropTypes.func.isRequired,
  onFolderClick: PropTypes.func.isRequired,
  onGroupIntoFolderClick: PropTypes.func.isRequired,
  onMoveToFolderClick: PropTypes.func.isRequired,
  onNewFolderClick: PropTypes.func.isRequired,
  onProjectArchiveClose: PropTypes.func.isRequired,
  onProjectDestroyClose: PropTypes.func.isRequired,
  onProjectToggle: PropTypes.func.isRequired,
  onProjectsPageChange: PropTypes.func.isRequired,
  onRenameFolderClick: PropTypes.func.isRequired,
  onSelectAllProjectsToggle: PropTypes.func.isRequired,
  onSidebarMenuSectionClick: PropTypes.func.isRequired,
  selectedFolderId: PropTypes.string,
  selectedProjectIds: PropTypes.array.isRequired,
};

function buildHasManyRelations({ inclusionType, relationType, resource }) {
  return resource.data?.relationships[relationType].data.map(
    (related) => resource.included[inclusionType][related.id],
  );
}

function buildHasOneRelation({ inclusionType, relationType, resource }) {
  return (
    resource.data?.relationships[relationType].data &&
    resource.included[inclusionType][
      resource.data.relationships[relationType].data.id
    ]
  );
}
